import { hoursForDate } from '../../../utils/weekdayHours';

/**
 * Date utility functions for the calendar.
 * Simple implementations to avoid additional dependencies.
 */

/**
 * Get the start of the week (Monday) for a given date.
 */
export function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
  return new Date(d.setDate(diff));
}

/**
 * Add days to a date.
 */
export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Add whole months to a date, pinned to the 1st so a 31st never overflows into the month after.
 */
export function addMonths(date, months) {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth() + months, 1);
}

/**
 * First day of the month of the given date (local time).
 */
export function startOfMonth(date) {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/**
 * Parse a plain 'YYYY-MM-DD' key (as the backend writes local dates) into a local Date,
 * without going through the browser's timezone the way new Date('YYYY-MM-DD') does.
 */
export function parseDateKey(key) {
  if (!key) return null;
  const [year, month, day] = String(key).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

/**
 * Add minutes to a date.
 */
export function addMinutes(date, minutes) {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() + minutes);
  return result;
}

/**
 * Format a date according to the given format string.
 * Supports: yyyy, MM, dd, HH, mm, ss
 */
export function format(date, formatStr) {
  const d = new Date(date);

  const pad = (n) => String(n).padStart(2, '0');

  const replacements = {
    yyyy: d.getFullYear(),
    MM: pad(d.getMonth() + 1),
    dd: pad(d.getDate()),
    HH: pad(d.getHours()),
    mm: pad(d.getMinutes()),
    ss: pad(d.getSeconds()),
  };

  let result = formatStr;
  for (const [key, value] of Object.entries(replacements)) {
    result = result.replace(key, value);
  }

  return result;
}

/**
 * Check if two dates are the same day.
 */
export function isSameDay(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

/**
 * Check if a date is today.
 */
export function isToday(date) {
  return isSameDay(date, new Date());
}

/**
 * Get Finnish month name.
 */
export function getFinnishMonth(date) {
  const months = [
    'tammikuuta', 'helmikuuta', 'maaliskuuta', 'huhtikuuta',
    'toukokuuta', 'kesäkuuta', 'heinäkuuta', 'elokuuta',
    'syyskuuta', 'lokakuuta', 'marraskuuta', 'joulukuuta'
  ];
  return months[new Date(date).getMonth()];
}

/**
 * Get Finnish weekday abbreviation.
 */
export function getFinnishWeekday(date) {
  const weekdays = ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La'];
  return weekdays[new Date(date).getDay()];
}

/**
 * Get ISO 8601 week number for a given date.
 */
export function getWeekNumber(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // Set to nearest Thursday: current date + 4 - current day number (Monday=1, Sunday=7)
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

/**
 * Format date range for header (e.g., "13 - 17 tammikuuta 2025").
 */
export function formatWeekRange(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const startDay = start.getDate();
  const endDay = end.getDate();
  const month = getFinnishMonth(end);
  const year = end.getFullYear();

  return `${startDay} - ${endDay} ${month} ${year}`;
}

/**
 * Parse ISO datetime string to Date object.
 */
export function parseISO(dateString) {
  return new Date(dateString);
}

/**
 * Get hour from a datetime (0-23).
 */
export function getHour(date) {
  return new Date(date).getHours();
}

/**
 * Get minutes from a datetime (0-59).
 */
export function getMinutes(date) {
  return new Date(date).getMinutes();
}

/**
 * Create a datetime from date and hour.
 */
export function setHour(date, hour, minute = 0) {
  const result = new Date(date);
  result.setHours(hour, Math.round(minute), 0, 0);
  return result;
}

/**
 * Get day index (0 = Monday, 4 = Friday).
 */
export function getDayIndex(date) {
  const day = new Date(date).getDay();
  return day === 0 ? 6 : day - 1; // Convert Sunday=0 to Monday=0
}

/**
 * Calculate duration in hours between two dates.
 */
export function getDurationHours(start, end) {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return (endDate - startDate) / (1000 * 60 * 60);
}

/**
 * Safely parse a time string (e.g., "08:00", "8:30", "8") to an hour number.
 * Returns defaultValue if parsing fails.
 */
export function parseTimeToHour(timeString, defaultValue = 8) {
  if (!timeString) return defaultValue;

  // Handle "HH:MM" format
  if (typeof timeString === 'string' && timeString.includes(':')) {
    const [hours, minutes] = timeString.split(':').map(Number);
    if (!isNaN(hours)) {
      const mins = !isNaN(minutes) ? minutes : 0;
      return hours + mins / 60;
    }
  }

  // Handle plain number string or number
  const parsed = parseInt(timeString, 10);
  return !isNaN(parsed) ? parsed : defaultValue;
}

/**
 * Get full Finnish weekday name.
 */
export function getFinnishWeekdayFull(date) {
  const weekdays = ['sunnuntai', 'maanantai', 'tiistai', 'keskiviikko', 'torstai', 'perjantai', 'lauantai'];
  return weekdays[new Date(date).getDay()];
}

/**
 * Get full English weekday name.
 */
export function getEnglishWeekdayFull(date) {
  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return weekdays[new Date(date).getDay()];
}

/**
 * Get full Swedish weekday name (lower case, as Swedish writes weekdays).
 */
export function getSwedishWeekdayFull(date) {
  const weekdays = ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'];
  return weekdays[new Date(date).getDay()];
}

/**
 * Get Swedish month name (lower case, as Swedish writes months).
 */
export function getSwedishMonth(date) {
  const months = [
    'januari', 'februari', 'mars', 'april',
    'maj', 'juni', 'juli', 'augusti',
    'september', 'oktober', 'november', 'december'
  ];
  return months[new Date(date).getMonth()];
}

/**
 * Get English month name.
 */
export function getEnglishMonth(date) {
  const months = [
    'January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'
  ];
  return months[new Date(date).getMonth()];
}

/**
 * Month title for the month view header.
 * Finnish: "Syyskuu 2026", Swedish: "september 2026", English: "September 2026"
 */
export function formatMonthTitle(date, language = 'fi') {
  const d = new Date(date);
  if (language === 'fi') {
    const months = [
      'tammikuu', 'helmikuu', 'maaliskuu', 'huhtikuu',
      'toukokuu', 'kesäkuu', 'heinäkuu', 'elokuu',
      'syyskuu', 'lokakuu', 'marraskuu', 'joulukuu'
    ];
    const month = months[d.getMonth()];
    return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${d.getFullYear()}`;
  }
  if (language === 'sv') {
    return `${getSwedishMonth(d)} ${d.getFullYear()}`;
  }
  return `${getEnglishMonth(d)} ${d.getFullYear()}`;
}

/**
 * Format date for day view header.
 * Finnish: "Tiistai 7. huhtikuuta 2026"
 * Swedish: "tisdag 7 april 2026"
 * English: "Tuesday 7 April 2026"
 */
export function formatDayDate(date, language = 'fi') {
  const d = new Date(date);
  if (language === 'fi') {
    const weekday = getFinnishWeekdayFull(d);
    // Capitalize first letter
    const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
    return `${capitalizedWeekday} ${d.getDate()}. ${getFinnishMonth(d)} ${d.getFullYear()}`;
  }
  if (language === 'sv') {
    return `${getSwedishWeekdayFull(d)} ${d.getDate()} ${getSwedishMonth(d)} ${d.getFullYear()}`;
  }
  return `${getEnglishWeekdayFull(d)} ${d.getDate()} ${getEnglishMonth(d)} ${d.getFullYear()}`;
}

/**
 * Parse work schedule start/end hours with safe defaults.
 */
export function parseWorkScheduleHours(workSchedule) {
  const DEFAULT_START = 8;
  const DEFAULT_END = 16;

  if (!workSchedule) {
    return { startHour: DEFAULT_START, endHour: DEFAULT_END };
  }

  return {
    startHour: parseTimeToHour(workSchedule.dayStart, DEFAULT_START),
    endHour: parseTimeToHour(workSchedule.dayEnd, DEFAULT_END),
  };
}

/**
 * Start/end hours of one date's work day: that weekday's hours from workSchedule.days, or the
 * schedule's bounds (dayStart/dayEnd) when the workshop is closed that day or no date is given.
 */
export function workScheduleHoursForDate(workSchedule, date) {
  const hours = date && workSchedule?.days ? hoursForDate(workSchedule.days, date) : null;
  return parseWorkScheduleHours(hours ? { dayStart: hours.start, dayEnd: hours.end } : workSchedule);
}

/**
 * Set a date's time of day from a fractional hour (14.5 = 14:30). Date.setHours() would truncate
 * the fraction, which matters now that a weekday can end at e.g. 14:30.
 */
export function setTimeOfDay(date, hour) {
  const minutes = Math.round(hour * 60);
  date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return date;
}

/** A date's time of day as a fractional hour (14:30 = 14.5). */
export function timeOfDay(date) {
  return date.getHours() + date.getMinutes() / 60;
}

/**
 * Whole-hour rows of a single-day timeline (the day view, the phone views): that day's work hours
 * rather than the whole week's, widened so that ``periods`` fit (bookings of the day, the shown
 * mechanics' own hours that day), each {start, end} in fractional hours. A closed day uses the
 * week's bounds. The "visible hours" filter, when set, wins as in the week grid.
 */
export function dayGridHours({ workSchedule, date, periods = [], filters = {} }) {
  const day = workSchedule?.days && date ? hoursForDate(workSchedule.days, date) : null;
  let start = parseTimeToHour(day ? day.start : workSchedule?.dayStart, 8);
  let end = parseTimeToHour(day ? day.end : workSchedule?.dayEnd, 16);
  periods.forEach((period) => {
    if (period && period.end > period.start) {
      start = Math.min(start, period.start);
      end = Math.max(end, period.end);
    }
  });
  if (filters.visibleStartHour !== null && filters.visibleStartHour !== undefined) {
    start = parseTimeToHour(filters.visibleStartHour, start);
  }
  if (filters.visibleEndHour !== null && filters.visibleEndHour !== undefined) {
    end = parseTimeToHour(filters.visibleEndHour, end);
  }
  const startHour = Math.max(0, Math.floor(start));
  const endHour = Math.min(24, Math.max(Math.ceil(end), startHour + 1));
  return { startHour, endHour };
}

/**
 * The hours an event needs the rows of ``dateKey`` ('yyyy-MM-dd') to show, as {start, end} in
 * fractional hours, or null. A single-day event needs its whole span. An event running over
 * several days needs only the hour where it starts or ends on that day; a day it runs through
 * entirely (or a whole-day absence) must not stretch the rows to 24 hours.
 */
export function eventRowsPeriod(event, dateKey) {
  if (!event?.local_start_date || !event?.local_end_date) return null;
  if (dateKey < event.local_start_date || dateKey > event.local_end_date) return null;
  const { local_start_hour: start, local_end_hour: end } = event;
  if (typeof start !== 'number' || typeof end !== 'number') return null;
  const startsToday = event.local_start_date === dateKey;
  const endsToday = event.local_end_date === dateKey;
  if (startsToday && endsToday) return { start, end };
  if (startsToday) return { start, end: Math.min(start + 1, 24) };
  if (endsToday) return { start: Math.max(end - 1, 0), end };
  return null;
}
